import { TCartItem } from '@/lib/cart/useCart';
import { ShopInfoNode } from '@/lib/types/stoq';
import { determineColorCode } from './colors';
import { parseHandle } from './handles';
import { determinePreorderSellingPlanInfo } from './preorders';
import { determineProductType } from './products';
import { determineVariants, determineSubmodel1, determineSubmodel2 } from './variants';
import { BaseCartLine } from '@/generated/graphql';
import { slugify } from '@/lib/utils';

export function mapShopifyCartToCartData(
  shopifyCartData: BaseCartLine,
  sellingPlans: ShopInfoNode
): TCartItem {
  const { merchandise, quantity, id, attributes, sellingPlanAllocation } = shopifyCartData;

  const { planIsPreorder, preorderDiscount, expectedInHouseDate } =
    determinePreorderSellingPlanInfo(sellingPlanAllocation, sellingPlans); // sellingplandata from "Stoq"

  const { product, title, image, price, compareAtPrice, selectedOptions } = merchandise;

  const variantCompareAtPrice = compareAtPrice?.amount;
  const variantPrice = price.amount;

  const productType = determineProductType(product?.handle);
  const make = product?.title.split(' ')[0];
  const model = product?.title.split(' ')[1]; // Assuming model is the second word in the title
  const selectedYearAttribute = attributes.find((a) => a.key === 'selected_year');
  const selectedYear = selectedYearAttribute ? selectedYearAttribute.value : null;

  const {
    display_color: extractedDisplayColor,
    year_generation: extractedYearRange,
    cab_size: extractedCabSize,
    model_detail: extractedModelDetail,
  } = parseHandle(product?.handle, slugify(make), slugify(model), productType);

  const determinedColor = determineColorCode(extractedDisplayColor || '');

  const {
    yearRange,
    bodyTrim,
    seatType,
    seatStyle,
    vehicleDetail,
    mirror,
    cabSize,
    bedSize,
    optionsMap,
    modelDetail,
    seatTypeOption,
    seatSubtype,
    seatConfigurations,
  } = determineVariants(selectedOptions);

  const selectOptionKeys = Array.from(optionsMap.keys());
  const cabSizeFound = cabSize || extractedCabSize;
  const modelDetailFound = modelDetail || extractedModelDetail || '';

  const determinedSubmodel1 = determineSubmodel1(
    productType,
    bodyTrim,
    vehicleDetail,
    cabSizeFound
  );

  const determinedSubmodel2 = determineSubmodel2(productType, vehicleDetail, bedSize, cabSizeFound);

  // Convert seatConfigurations array to Record format for cart
  const selectedSeatConfigurations: Record<string, string> = {};
  if (seatConfigurations && seatConfigurations.length > 0) {
    seatConfigurations.forEach((config) => {
      selectedSeatConfigurations[config.seatTypeOption] = config.seatSubtype;
    });
  }

  return {
    id: id,
    sku: merchandise?.sku, // Assuming SKU comes from variant ID
    parent_generation: yearRange || extractedYearRange, // Adjust as necessary
    year_generation: yearRange || extractedYearRange,
    make: product?.title.split(' ')[0] || null,
    model: product?.title.split(' ')[1] || null,
    submodel1: determinedSubmodel1, // Adjust as necessary
    submodel2: determinedSubmodel2,
    submodel3: mirror || seatStyle,
    feature: image?.url || null, // Assuming this is the product image
    product: image?.url || null, // Assuming single image for now
    product_video_carousel_thumbnail: null, // Add logic if available
    product_video_carousel: null,
    product_video_zoom: null,
    product_video_360: null,
    banner: image?.url || null, // Assuming banner is the product image for now
    type: productType || 'Car Cover',
    year_options: yearRange || extractedYearRange,
    make_slug: make?.toLowerCase() || null,
    model_slug: model?.toLowerCase() || null,
    msrp: parseFloat(variantPrice || '0'),
    price: parseFloat(variantCompareAtPrice || '0'),
    quantity: quantity || 1,
    display_color: determinedColor,
    display_id: null, // Add logic if available
    display_set: seatType,
    skuLabsSKU: null, // Add logic if available
    preorder: planIsPreorder,
    preorder_discount: preorderDiscount,
    preorder_date: expectedInHouseDate,
    gtin: null, // Add logic if available
    mpn: null, // Add logic if available
    title: product?.title,
    variantTitle: title,
    variant1Name: selectOptionKeys?.[0] ?? '',
    variant2Name: selectOptionKeys?.[1] ?? '',
    variant3Name: selectOptionKeys?.[2] ?? '',
    variant4Name: selectOptionKeys?.[2] ?? '',
    variant5Name: selectOptionKeys?.[3] ?? '',
    model_detail: modelDetailFound ?? '',
    selected_year: selectedYear,
    productId: product?.id || '',
    productVariantId: merchandise?.id || '',
    seat_type_option: seatTypeOption,
    seat_subtype: seatSubtype,
    selectedSeatConfigurations:
      Object.keys(selectedSeatConfigurations).length > 0 ? selectedSeatConfigurations : undefined,
  };
}
